import { useEffect } from 'react';
import cn from 'classnames';
import Flex from '@creditflowresearch/uilib/Flex';
import { Tile, TileHeader, TileTabsRow } from '@creditflowresearch/uilib/Tile';
import Button from '@creditflowresearch/uilib/Button';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import { getBondsDate, getBondsFullDate } from '@/creditflowresearch/ui/components/bonds/helpers';
import { getFormatUrl } from '@/creditflowresearch/ui/common/helpers';
import { Link } from '@framework/router/Next/Link';
import URL from '@/creditflowresearch/Router/urlConstants';
import { BWICS, INVENTORIES } from '@/creditflowresearch/domain/BondType';
import InventorySubscribeButton from '@/creditflowresearch/ui/components/bonds/common/InventoriesSubscribeButtonContainer';
import BwicSubscribeButton from '@/creditflowresearch/ui/components/bonds/common/BwicsSubscribeButtonContainer';
import SearchContainer from '@/creditflowresearch/ui/components/bonds/common/SearchContainer';
import BondTypes from '@/creditflowresearch/ui/components/bonds/common/BondTypes/BondTypes';
import BondsChartsContextProvider, {
  useBondsChartsContext,
} from '@/creditflowresearch/application/Bonds/charts/BondsChartsContext';
import Charts from '@/creditflowresearch/ui/components/bonds/Charts';
import BondTables from '@/creditflowresearch/ui/components/bonds/common/BondTables';
import TableFooterLabel from '@/creditflowresearch/ui/components/bonds/common/BondTables/TableFooterLabel';
import { getColumns as getBwicHeaderColumns } from '@/creditflowresearch/ui/components/bonds/common/BondTables/Bwic/BwicColumns';
import BwicRow from '@/creditflowresearch/ui/components/bonds/common/BondTables/Bwic/BwicRow';
import {
  getColumns as getInventoryHeaderColumns,
} from '@/creditflowresearch/ui/components/bonds/common/BondTables/Inventory/InventoryColumns';
import InventoryRow from '@/creditflowresearch/ui/components/bonds/common/BondTables/Inventory/InventoryRow';
import { getQueryString, useRouter } from '@framework/router';
import { useBondsAssetClassesContext } from '@/creditflowresearch/application/Bonds/BondsAssetClassesContext';
import { useBwicTableContext } from '@/creditflowresearch/application/Bonds/BwicTableContext';
import { useInventoryTableContext } from '@/creditflowresearch/application/Bonds/InventoryTableContext';
import ExportToExcelButton from '@/creditflowresearch/ui/components/bonds/common/ExportToExcelButtonContainer';
import ChartShowButton from '@/creditflowresearch/ui/components/bonds/common/ChartShowButton';
import { isScreenXs } from '@creditflowresearch/uilib/utils/screen';
import { useTrackAnalytics } from '@/creditflowresearch/application/useTrackAnalytics';
import { BI_MODULE } from '@/condor/ui/components/FinsightManager/constants';
import TabSelect from '@/creditflowresearch/ui/components/bondscreener/TabSelect';
import FiltersBtnContainer from '@/creditflowresearch/ui/components/bonds/common/FiltersButtonContainer';

import styles from './bondsPageContent.module.scss';
import chartStyles from '@/creditflowresearch/ui/components/bonds/Charts/charts.module.scss';

const BOND_TYPE_TABS: Record<string, { name: string }> = {
  [BWICS]: { name: 'BWICS' },
  [INVENTORIES]: { name: 'Inventory' },
};

interface IBondsPageContentProps {
  onFiltersToggle?: () => void,
}

const BondsPageContent = ({ onFiltersToggle }: IBondsPageContentProps) => {
  const router: any = useRouter();
  const {
    query: {
      bondType,
      assetClass,
      products,
      regions,
    },
  } = router;
  const search = getQueryString({ products, regions });
  const { isShowCharts, setIsShowCharts } = useBondsChartsContext();
  const { currentAssetClass, assetsClasses } = useBondsAssetClassesContext();
  const {
    bwicsData: {
      lastImportAt: bwicLastImportAt,
      subscriptionStatus: { subscriptionRequiredPlan: bwicSubscriptionRequiredPlan },
    },
  } = useBwicTableContext();
  const {
    inventoriesData: {
      lastImportAt: inventoriesLastImportAt,
      subscriptionStatus: { subscriptionRequiredPlan: inventorySubscriptionRequiredPlan },
    },
  } = useInventoryTableContext();
  const { trackAnalytics } = useTrackAnalytics();

  const isAssetsInitialized = assetsClasses.length > 0;
  const assetLastUpdate = bondType === BWICS ? bwicLastImportAt : inventoriesLastImportAt;
  const isMobile = isScreenXs();

  useEffect(() => {
    setIsShowCharts(!isMobile);
    trackAnalytics({ module: BI_MODULE });
  }, []);

  return (
    <Tile dataTest="bondsPageContent">
      <Spinner overlay isVisible={ !isAssetsInitialized } />
      <TileHeader
        title={ <h3 data-test="bondsPageTitle">BWICS &amp; Inventory</h3> }
        hideTitleOnDesktop
        noDesktopBottomBorder
        className={ styles.tileHeader }
        dataTest="bondsPageHeader"
      >
        <Flex
          direction={ { sm: 'column', md: 'column', xl: 'row' } }
          align={ { sm: 'stretch', md: 'stretch', xl: 'center' } }
          gap={ 10 }
        >
          <Flex gap={ 10 } align="center" className={ styles.actionsRow }>
            { assetLastUpdate && (
              <Tooltip content={ getBondsFullDate(assetLastUpdate) } asChild>
                <Button
                  as={ Link }
                  to={ getFormatUrl(`${ URL.BONDS_SUMMARY_BASE }-${ bondType }-${ assetClass }?${ search }`) }
                  variant="outline"
                  size="S"
                  fit={ { sm: 'full', md: 'full' } }
                  dataTest="lastUpdated"
                  ariaLabel="Last Updated"
                >
                  { `Last Updated: ${ getBondsDate(assetLastUpdate, true) }` }
                </Button>
              </Tooltip>
            ) }
            { bondType === INVENTORIES
              ? (
                <InventorySubscribeButton
                  isSubscribeAvailable
                  requiredPlan={ inventorySubscriptionRequiredPlan }
                  variant="outline"
                  size="S"
                />
              )
              : (
                <BwicSubscribeButton
                  isSubscribeAvailable
                  requiredPlan={ bwicSubscriptionRequiredPlan }
                  variant="outline"
                  size="S"
                />
              ) }
            <ExportToExcelButton dataTest="exportButtonWithLoader" />
            <ChartShowButton
              isChartVisible={ isShowCharts }
              onClick={ () => setIsShowCharts(!isShowCharts) }
            />
          </Flex>
          <div className={ styles.tabSelect }>
            <TabSelect
              tabs={ BOND_TYPE_TABS }
              activeTab={ bondType || BWICS }
              onChangeTab={ (tab: string) => {
                const tabUrl = tab === BWICS ? URL.BONDS_BWICS : URL.BONDS_INVENTORIES;
                router.push(getFormatUrl(`${ tabUrl }-${ assetClass }?${ search }`));
              } }
              dataTest="bondsPageTabSelect"
            />
          </div>
          <div className={ styles.searchWrapper }>
            <SearchContainer />
          </div>
          { onFiltersToggle && (
            <FiltersBtnContainer onClick={ onFiltersToggle } className={ styles.filtersButton } />
          ) }
        </Flex>
      </TileHeader>
      <TileTabsRow dataTest="bondsPageTabs">
        <BondTypes assetClass={ assetClass } />
      </TileTabsRow>
      <div className={ cn(chartStyles.chartsWrapper, { [chartStyles.isShowCharts]: isShowCharts }) }>
        { isShowCharts && <Charts /> }
      </div>
      <BondTables
        tableFooterLabel={ <TableFooterLabel /> }
        getBwicHeaderColumns={ getBwicHeaderColumns }
        bwicRow={ BwicRow }
        getInventoryHeaderColumns={ getInventoryHeaderColumns }
        inventoryRow={ InventoryRow }
        currentAssetClass={ currentAssetClass }
      />
    </Tile>
  );
};

export default (props: IBondsPageContentProps) => (
  <BondsChartsContextProvider>
    <BondsPageContent { ...props } />
  </BondsChartsContextProvider>
);
